import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { spacing } from "../../../design-system/tokens/spacing";
import type { SidebarProfileChipPlacement } from "./SidebarProfileChip.types";

const toPx = (token: string) => parseFloat(token) || 0;
const GAP = toPx(spacing[1]);
const VIEWPORT_MARGIN = toPx(spacing[2]);

export interface MenuPosition {
  top: number;
  left: number;
}

/** Open state, photo-load failure, refs, trigger-anchored position and listeners for the profile chip. */
export function useSidebarProfileChipInternalState(placement: SidebarProfileChipPlacement, avatarUrl?: string | null) {
  const [open, setOpen] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => setPhotoFailed(false), [avatarUrl]);
  const onPhotoError = useCallback(() => setPhotoFailed(true), []);
  const [position, setPosition] = useState<MenuPosition>({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const floating = placement !== "top-start";

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  // Anchor the portaled menu to the trigger, then clamp it inside the viewport.
  useLayoutEffect(() => {
    if (!open || !floating || !wrapperRef.current || !menuRef.current) return;
    const trigger = wrapperRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const rawTop = placement === "right-end" ? trigger.bottom - menu.height : trigger.bottom + GAP;
    const rawLeft = placement === "right-end" ? trigger.right + GAP : trigger.right - menu.width;
    const maxTop = window.innerHeight - menu.height - VIEWPORT_MARGIN;
    const maxLeft = window.innerWidth - menu.width - VIEWPORT_MARGIN;
    setPosition({
      top: Math.max(VIEWPORT_MARGIN, Math.min(rawTop, maxTop)),
      left: Math.max(VIEWPORT_MARGIN, Math.min(rawLeft, maxLeft)),
    });
  }, [open, floating, placement]);

  // A fixed menu would drift from its trigger on resize or scroll, so it closes instead.
  useEffect(() => {
    if (!open || !floating) return;
    const handleMove = (e: Event) => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return;
      close();
    };
    window.addEventListener("resize", handleMove);
    window.addEventListener("scroll", handleMove, true);
    return () => {
      window.removeEventListener("resize", handleMove);
      window.removeEventListener("scroll", handleMove, true);
    };
  }, [open, floating, close]);

  // Click outside (the portaled menu counts as inside) closes the menu.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, close]);

  // Escape closes the menu.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, close]);

  return { open, close, toggle, photoFailed, onPhotoError, position, floating, wrapperRef, menuRef };
}
